import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { errorHandler } from "_services/errorHandler";
import {
  useTransportResourceServiceV1GetVendorDetailQuery,
  useTransportResourceServiceV1UpdateVendorMutation,
  useTransportResourceServiceV1UploadVendorLogoMutation,
  useTransportResourceServiceV1CreateVendorBranchMutation,
  useTransportResourceServiceV1UpdateVendorBranchMutation,
  useTransportResourceServiceV1DeleteVendorBranchMutation,
  useTransportResourceServiceV1CreateVendorAddressMutation,
  useTransportResourceServiceV1UpdateVendorAddressMutation,
  useTransportResourceServiceV1DeleteVendorAddressMutation,
  TransportResourceGetVendorDetailRes,
  TransportResourceServiceV1UploadVendorLogoBody,
  TransportResourceServiceV1UpdateVendorBody,
  vendorApi,
} from "_services/modules/transport-resource/vendor";
import {
  VendorAddressFormValues,
  VendorAddressItem,
  VendorBranchFormValues,
  VendorBranchItem,
  VendorInformationFormValues,
  VendorAddressCategory,
  ExtendedBranchItem,
  ExtendedAddressItem,
  ExtendedUpdateVendorBody,
} from "_interfaces/vendor.interfaces";
import { useAppTranslation } from "locale/useAppTranslation";
import { vendorPageRouteName } from "modules/master-data/vendor/list/index.page";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { isHeadquarterCategory, getAvailableBranchOptions } from "data/address-category";

const vendorInfoSchema = yup
  .object({
    vendorType: yup
      .mixed<"individual" | "company">()
      .oneOf(["individual", "company"])
      .required(),
    vendorName: yup.string().required("Vendor name is required"),
    vendorId: yup.string().notRequired(),
    npwp: yup.string().required("NPWP is required"),
    email: yup.string().email("Please enter a valid email").required("Vendor email is required"),
    contactPersonName: yup.string().required("Contact person name is required"),
    contactPersonPhoneCountryCode: yup.string().required(),
    contactPersonPhoneNumber: yup
      .string()
      .required("Contact person phone number is required"),
    legalName: yup.string().when("vendorType", {
      is: "company",
      then: (schema) => schema.required("Legal name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    status: yup.mixed<"active" | "inactive">().oneOf(["active", "inactive"]).required(),
  })
  .required();

const vendorBranchSchema = yup
  .object({
    branchName: yup.string().required("Branch name is required"),
    picName: yup.string().required("PIC name is required"),
    picEmail: yup
      .string()
      .email("Please enter a valid email")
      .required("PIC email is required"),
    picCountryCode: yup.string().required(),
    picPhoneNumber: yup.string().required("PIC phone number is required"),
  })
  .required();

const vendorAddressSchema = yup
  .object({
    addressName: yup.string().required("Address name is required"),
    addressCategory: yup.string().required("Address category is required"),
    branch: yup.string().required("Branch is required"),
    country: yup.string().required("Country is required"),
    province: yup.string().required("Province is required"),
    city: yup.string().required("City is required"),
    postalCode: yup.string().required("Postal code is required"),
    fullAddress: yup.string().required("Full address is required"),
    mapsUrl: yup.string().required("Google Maps URL is required"),
    isBillingAddress: yup.boolean().default(false),
  })
  .required();

interface Snapshot {
  vendorInfo: VendorInformationFormValues;
  branches: ExtendedBranchItem[];
  addresses: ExtendedAddressItem[];
}

const parsePicPhone = (phone = "") => {
  if (phone.startsWith("+62")) {
    return { countryCode: "+62", number: phone.slice(3) };
  }
  if (phone.startsWith("+")) {
    return { countryCode: phone.slice(0, 3), number: phone.slice(3) };
  }
  return { countryCode: "+62", number: phone };
};

const useEditVendor = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useAppTranslation("vendor");

  if (!id) {
    throw new Error("Vendor ID is required for editing");
  }

  const [updateVendor, { isLoading: isUpdatingVendor }] = useTransportResourceServiceV1UpdateVendorMutation();
  const [createVendorBranch] = useTransportResourceServiceV1CreateVendorBranchMutation();
  const [updateVendorBranch] = useTransportResourceServiceV1UpdateVendorBranchMutation();
  const [deleteVendorBranch] = useTransportResourceServiceV1DeleteVendorBranchMutation();
  const [createVendorAddress] = useTransportResourceServiceV1CreateVendorAddressMutation();
  const [updateVendorAddress] = useTransportResourceServiceV1UpdateVendorAddressMutation();
  const [deleteVendorAddress] = useTransportResourceServiceV1DeleteVendorAddressMutation();
  const [uploadLogo] = useTransportResourceServiceV1UploadVendorLogoMutation();

  const { data: detail, isLoading: isLoadingDetail, refetch } = useTransportResourceServiceV1GetVendorDetailQuery(
    { id },
    { skip: !id },
  );

  const vendorInfoForm = useForm<VendorInformationFormValues>({
    mode: "onChange",
    resolver: yupResolver(vendorInfoSchema),
    defaultValues: {
      vendorType: "company",
      vendorName: "",
      vendorId: "",
      npwp: "",
      email: "",
      contactPersonName: "",
      contactPersonPhoneCountryCode: "+62",
      contactPersonPhoneNumber: "",
      legalName: "",
      status: "active",
      vendorLogo: "",
    },
  });

  const vendorBranchForm = useForm<VendorBranchFormValues>({
    mode: "onChange",
    resolver: yupResolver(vendorBranchSchema),
    defaultValues: {
      branchName: "",
      picName: "",
      picEmail: "",
      picCountryCode: "+62",
      picPhoneNumber: "",
    },
  });

  const vendorAddressForm = useForm<VendorAddressFormValues>({
    mode: "onChange",
    resolver: yupResolver(vendorAddressSchema),
    defaultValues: {
      addressName: "",
      addressCategory: "branch",
      branch: "",
      country: "",
      province: "",
      city: "",
      postalCode: "",
      fullAddress: "",
      mapsUrl: "",
      isBillingAddress: false,
    },
  });

  const [openSections, setOpenSections] = useState({
    vendorInformation: true,
    vendorBranch: false,
    vendorAddress: false,
  });

  const [branchItems, setBranchItems] = useState<ExtendedBranchItem[]>([]);
  const [addressItems, setAddressItems] = useState<ExtendedAddressItem[]>([]);
  const [editingBranchIndex, setEditingBranchIndex] = useState<number | null>(null);
  const [editingAddressIndex, setEditingAddressIndex] = useState<number | null>(null);
  const [originalSnapshot, setOriginalSnapshot] = useState<Snapshot | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [resultModal, setResultModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    icon: "success" | "danger" | "warning" | "info";
  }>({
    open: false,
    title: "",
    message: "",
    icon: "success",
  });

  useEffect(() => {
    if (detail) {
      const vendorDetail = detail as TransportResourceGetVendorDetailRes & {
        contact_person_phone_number?: string;
        email?: string;
        contact_person_name?: string;
      };

      const parsedContactPhone = parsePicPhone(
        vendorDetail.contact_person_phone_number || "+62",
      );

      const vendorInfo: VendorInformationFormValues = {
        vendorType:
          vendorDetail.vendor_type === "individual" ||
            vendorDetail.vendor_type === "company"
            ? vendorDetail.vendor_type
            : "company",

        vendorName: vendorDetail.vendor_name || "",

        vendorId: vendorDetail.vendor_code || "",

        npwp: vendorDetail.npwp || "",

        email: vendorDetail.email || "",

        contactPersonName:
          vendorDetail.contact_person_name || "",

        contactPersonPhoneCountryCode:
          parsedContactPhone.countryCode,

        contactPersonPhoneNumber:
          parsedContactPhone.number,

        legalName: vendorDetail.legal_name || "",

        status:
          vendorDetail.status === "inactive"
            ? "inactive"
            : "active",

        vendorLogo: vendorDetail.vendor_logo || "",
      };

      vendorInfoForm.reset(vendorInfo);

      const mappedBranches: ExtendedBranchItem[] = (detail.branches || []).map((b) => {
        const { countryCode, number } = parsePicPhone(b.pic_phone);
        return {
          id: b.id,
          branchName: b.branch_name || "",
          picName: b.pic_name || "",
          picEmail: b.pic_email || "",
          picCountryCode: countryCode,
          picPhoneNumber: number,
        };
      });

      const mappedAddresses: ExtendedAddressItem[] = (detail.branches || []).flatMap((b) =>
        (b.addresses || []).map((a: any) => ({
          id: a.id,
          addressName: a.address_name || "",
          addressCategory: (a.address_category as VendorAddressCategory) || "branch",
          branch: b.branch_name || "",
          fullAddress: a.full_address || "",
          country: a.country_id || a.country || "Indonesia",
          province: a.province_id || a.province || "",
          city: a.city_id || a.city || "",
          postalCode: a.postal_code || "",
          mapsUrl: a.google_maps_url || "",
          isBillingAddress: !!a.is_billing_address,
          countryName: a.country_name || a.country || "Indonesia",
          provinceName: a.province_name || a.province || a.state_name,
          cityName: a.city_name || a.city,
        })),
      );

      setBranchItems(mappedBranches);
      setAddressItems(mappedAddresses);
      setOriginalSnapshot({
        vendorInfo,
        branches: mappedBranches,
        addresses: mappedAddresses,
      });
    }
  }, [detail, vendorInfoForm]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: "Edit Vendor Data",
        hasBackButton: true,
      }),
    );
  }, [dispatch]);

  const branchOptions = useMemo(
    () => getAvailableBranchOptions(branchItems, addressItems, editingAddressIndex),
    [branchItems, addressItems, editingAddressIndex],
  );

  const hasBillingAddress = useMemo(
    () => addressItems.some((item) => item.isBillingAddress),
    [addressItems],
  );

  const isDirty = useMemo(() => {
    if (!originalSnapshot) return false;

    const currentInfo = vendorInfoForm.getValues();
    const infoChanged =
      currentInfo.vendorName !== originalSnapshot.vendorInfo.vendorName ||
      currentInfo.vendorType !== originalSnapshot.vendorInfo.vendorType ||
      currentInfo.npwp !== originalSnapshot.vendorInfo.npwp ||
      currentInfo.email !== originalSnapshot.vendorInfo.email ||
      currentInfo.contactPersonName !== originalSnapshot.vendorInfo.contactPersonName ||
      currentInfo.contactPersonPhoneCountryCode !== originalSnapshot.vendorInfo.contactPersonPhoneCountryCode ||
      currentInfo.contactPersonPhoneNumber !== originalSnapshot.vendorInfo.contactPersonPhoneNumber ||
      currentInfo.legalName !== originalSnapshot.vendorInfo.legalName ||
      currentInfo.status !== originalSnapshot.vendorInfo.status ||
      currentInfo.vendorLogo !== originalSnapshot.vendorInfo.vendorLogo ||
      Boolean(currentInfo.vendorLogoFile);

    if (infoChanged) return true;

    if (branchItems.length !== originalSnapshot.branches.length) return true;
    const branchesChanged = branchItems.some((b, idx) => {
      const snapB = originalSnapshot.branches[idx];
      return (
        b.branchName !== snapB.branchName ||
        b.picName !== snapB.picName ||
        b.picEmail !== snapB.picEmail ||
        b.picCountryCode !== snapB.picCountryCode ||
        b.picPhoneNumber !== snapB.picPhoneNumber
      );
    });
    if (branchesChanged) return true;

    if (addressItems.length !== originalSnapshot.addresses.length) return true;
    const addressesChanged = addressItems.some((a, idx) => {
      const snapA = originalSnapshot.addresses[idx];
      return (
        a.addressName !== snapA.addressName ||
        a.addressCategory !== snapA.addressCategory ||
        a.branch !== snapA.branch ||
        a.fullAddress !== snapA.fullAddress ||
        a.province !== snapA.province ||
        a.city !== snapA.city ||
        a.postalCode !== snapA.postalCode ||
        a.mapsUrl !== snapA.mapsUrl ||
        a.isBillingAddress !== snapA.isBillingAddress
      );
    });
    return addressesChanged;
  }, [originalSnapshot, vendorInfoForm, branchItems, addressItems]);

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const openSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: true,
    }));
  };

  const handleNextFromInformation = async () => {
    setOpenSections((current) => ({
      ...current,
      vendorInformation: false,
      vendorBranch: true,
    }));
  };

  const handleNextFromBranch = async () => {
    setOpenSections((current) => ({
      ...current,
      vendorBranch: false,
      vendorAddress: true,
    }));
  };

  const handleAddBranch = async () => {
    const isValid = await vendorBranchForm.trigger();
    if (!isValid) {
      return;
    }

    const values = vendorBranchForm.getValues();
    const nextBranch: ExtendedBranchItem = {
      branchName: values.branchName,
      picName: values.picName,
      picEmail: values.picEmail,
      picCountryCode: values.picCountryCode,
      picPhoneNumber: values.picPhoneNumber,
    };

    if (editingBranchIndex !== null) {
      const oldBranchName = branchItems[editingBranchIndex].branchName;
      const newBranchName = values.branchName;
      setBranchItems((current) =>
        current.map((item, index) =>
          index === editingBranchIndex ? { ...item, ...nextBranch } : item,
        ),
      );
      if (oldBranchName !== newBranchName) {
        setAddressItems((current) =>
          current.map((item) =>
            item.branch === oldBranchName ? { ...item, branch: newBranchName } : item,
          ),
        );
      }
      setEditingBranchIndex(null);
    } else {
      setBranchItems((current) => [...current, nextBranch]);
    }

    vendorBranchForm.reset({
      branchName: "",
      picName: "",
      picEmail: "",
      picCountryCode: "+62",
      picPhoneNumber: "",
    });
  };

  const handleEditBranch = (index: number) => {
    const item = branchItems[index];
    setEditingBranchIndex(index);
    vendorBranchForm.reset({
      branchName: item.branchName,
      picName: item.picName,
      picEmail: item.picEmail,
      picCountryCode: item.picCountryCode,
      picPhoneNumber: item.picPhoneNumber,
    });
  };

  const handleDeleteBranch = (index: number) => {
    if (branchItems.length <= 1) {
      toast.error(t("toast-branch-required") || "Vendor must have at least one branch");
      return;
    }
    const deletedBranchName = branchItems[index].branchName;
    setBranchItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setAddressItems((current) => current.filter((item) => item.branch !== deletedBranchName));
    if (editingBranchIndex === index) {
      setEditingBranchIndex(null);
    }
  };

  const handleAddAddress = async () => {
    const isValid = await vendorAddressForm.trigger();
    if (!isValid) {
      return;
    }

    const values = vendorAddressForm.getValues();
    const nextAddress: ExtendedAddressItem = {
      addressName: values.addressName,
      addressCategory: values.addressCategory,
      branch: values.branch,
      fullAddress: values.fullAddress,
      country: values.country,
      province: values.province,
      city: values.city,
      postalCode: values.postalCode,
      mapsUrl: values.mapsUrl,
      isBillingAddress: values.isBillingAddress,
      countryName: values.countryName || (values.country === "102" ? "Indonesia" : values.country),
      provinceName: values.provinceName || values.province,
      cityName: values.cityName || values.city,
    };

    const targetBranchIndex = branchItems.findIndex((branch) => branch.branchName === values.branch);
    if (targetBranchIndex === -1) {
      toast.error(t("toast-invalid-branch-link") || "Invalid branch selection");
      return;
    }

    const isBranchAlreadyUsed = addressItems.some(
      (item, index) => index !== editingAddressIndex && item.branch === values.branch,
    );
    if (isBranchAlreadyUsed) {
      toast.error("Branch already has an address assigned");
      return;
    }

    if (isHeadquarterCategory(values.addressCategory)) {
      const isHqAlreadyUsed = addressItems.some(
        (item, index) => index !== editingAddressIndex && isHeadquarterCategory(item.addressCategory),
      );
      if (isHqAlreadyUsed) {
        toast.error("Only one Headquarter address is allowed");
        return;
      }
    }

    if (editingAddressIndex !== null) {
      setAddressItems((current) =>
        current.map((item, index) =>
          index === editingAddressIndex ? nextAddress : item,
        ),
      );
      setEditingAddressIndex(null);
    } else {
      setAddressItems((current) => [...current, nextAddress]);
    }

    vendorAddressForm.reset({
      addressName: "",
      addressCategory: "branch",
      branch: "",
      country: "",
      province: "",
      city: "",
      postalCode: "",
      fullAddress: "",
      mapsUrl: "",
      isBillingAddress: false,
      countryName: "",
      provinceName: "",
      cityName: "",
    });
  };

  const handleEditAddress = (index: number) => {
    const item = addressItems[index];
    setEditingAddressIndex(index);
    vendorAddressForm.reset({
      addressName: item.addressName,
      addressCategory: item.addressCategory,
      branch: item.branch,
      country: item.country,
      province: item.province,
      city: item.city,
      postalCode: item.postalCode,
      fullAddress: item.fullAddress,
      mapsUrl: item.mapsUrl,
      isBillingAddress: item.isBillingAddress,
      countryName: item.countryName,
      provinceName: item.provinceName,
      cityName: item.cityName,
    });
  };

  const handleDeleteAddress = (index: number) => {
    const targetAddress = addressItems[index];
    if (targetAddress) {
      const branchAddressesCount = addressItems.filter(
        (item) => item.branch === targetAddress.branch,
      ).length;
      if (branchAddressesCount <= 1) {
        toast.error(
          t("toast-all-branches-must-have-address") ||
            "Each branch must have at least one address",
        );
        return;
      }
    }
    setAddressItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    if (editingAddressIndex === index) {
      setEditingAddressIndex(null);
    }
  };

  const handleConfirmAddress = () => {
    if (addressItems.length === 0) {
      toast.error(t("toast-address-required") || "Address is required");
      return;
    }
    if (!hasBillingAddress) {
      toast.error(t("toast-billing-required") || "Billing address is required");
      return;
    }
    toast.success(t("toast-address-confirmed") || "Address confirmed");
  };

  const handleCancel = () => {
    if (isDirty) {
      const confirmLeave = window.confirm(
        // @ts-expect-error temporary i18n key typing limitation
        t("confirm-unsaved-changes") || "You have unsaved changes. Are you sure you want to leave?",
      );
      if (!confirmLeave) return;
    }
    navigate(`/master-data/vendor/${id}/detail`);
  };

  const handleSave = async () => {
    const isValidVendorInfo = await vendorInfoForm.trigger();
    if (!isValidVendorInfo) {
      openSection("vendorInformation");
      toast.error(t("toast-complete-required-fields") || "Please complete all required fields");
      return;
    }

    if (branchItems.length === 0) {
      openSection("vendorBranch");
      toast.error(t("toast-branch-required") || "At least one branch is required");
      return;
    }

    if (addressItems.length === 0) {
      openSection("vendorAddress");
      toast.error(t("toast-address-required") || "At least one address is required");
      return;
    }

    const hasAddressForEveryBranch = branchItems.every((branch) =>
      addressItems.some((address) => address.branch === branch.branchName),
    );

    if (!hasAddressForEveryBranch) {
      openSection("vendorAddress");
      toast.error(t("toast-all-branches-must-have-address") || "All branches must have at least one address");
      return;
    }

    if (!hasBillingAddress) {
      openSection("vendorAddress");
      toast.error(t("toast-billing-required") || "One billing address is required");
      return;
    }

    const cleanPhone = (countryCode: string, phoneNumber: string) => {
      const code = countryCode.replace(/\s+/g, "");
      let num = phoneNumber.replace(/\D/g, "");
      if (num.startsWith("0")) {
        num = num.slice(1);
      }
      return `${code}${num}`;
    };

    try {
      setIsSaving(true);

      const vendorInfo = vendorInfoForm.getValues();
      const payload: TransportResourceServiceV1UpdateVendorBody = {
        vendor_name: vendorInfo.vendorName,
        legal_name:
          vendorInfo.vendorType === "company" && vendorInfo.legalName
            ? vendorInfo.legalName
            : undefined,
        npwp: vendorInfo.npwp,
        vendor_type: vendorInfo.vendorType,
        status: vendorInfo.status,
        email: vendorInfo.email,
        contact_person_name: vendorInfo.contactPersonName,
        contact_person_phone_number: cleanPhone(
          vendorInfo.contactPersonPhoneCountryCode,
          vendorInfo.contactPersonPhoneNumber,
        ),
      };

      await updateVendor({
        id,
        transportResourceServiceV1UpdateVendorBody: payload,
      }).unwrap();

      // 1. Delete removed branches
      const currentBranchIds = new Set(branchItems.map((b) => b.id).filter(Boolean));
      const deletedBranches = (originalSnapshot?.branches || []).filter(
        (snapB) => snapB.id && !currentBranchIds.has(snapB.id),
      );
      for (const delB of deletedBranches) {
        if (delB.id) {
          await deleteVendorBranch({ vendorId: id, branchId: delB.id }).unwrap();
        }
      }

      // 2. Create or update branches
      for (const b of branchItems) {
        const phoneFormatted = cleanPhone(b.picCountryCode, b.picPhoneNumber);
        if (b.id) {
          const snapB = originalSnapshot?.branches.find((sb) => sb.id === b.id);
          const branchChanged =
            !snapB ||
            b.branchName !== snapB.branchName ||
            b.picName !== snapB.picName ||
            b.picEmail !== snapB.picEmail ||
            b.picCountryCode !== snapB.picCountryCode ||
            b.picPhoneNumber !== snapB.picPhoneNumber;

          if (branchChanged) {
            await updateVendorBranch({
              vendorId: id,
              branchId: b.id,
              transportResourceServiceV1UpdateVendorBranchBody: {
                branch_name: b.branchName,
                pic_name: b.picName,
                pic_email: b.picEmail,
                pic_phone: phoneFormatted,
              },
            }).unwrap();
          }
        } else {
          await createVendorBranch({
            vendorId: id,
            transportResourceServiceV1CreateVendorBranchBody: {
              branch_name: b.branchName,
              pic_name: b.picName,
              pic_email: b.picEmail,
              pic_phone: phoneFormatted,
            },
          }).unwrap();
        }
      }

      // 3. Refetch to get updated branch IDs
      const refetchedRes = await refetch().unwrap();
      const branchNameToIdMap: Record<string, string> = {};
      (refetchedRes.branches || []).forEach((b) => {
        if (b.branch_name && b.id) {
          branchNameToIdMap[b.branch_name] = b.id;
        }
      });

      // 4. Delete removed addresses
      const currentAddressIds = new Set(addressItems.map((a) => a.id).filter(Boolean));
      const deletedAddresses = (originalSnapshot?.addresses || []).filter(
        (snapA) => snapA.id && !currentAddressIds.has(snapA.id),
      );
      for (const delA of deletedAddresses) {
        if (delA.id) {
          await deleteVendorAddress({ vendorId: id, addressId: delA.id }).unwrap();
        }
      }

      // 5. Create or update addresses
      for (const a of addressItems) {
        const targetBranchId = branchNameToIdMap[a.branch];
        if (!targetBranchId) {
          continue;
        }

        const addressBody = {
          branch_id: targetBranchId,
          address_name: a.addressName,
          address_category: a.addressCategory,
          full_address: a.fullAddress,
          province_id: a.province,
          city_id: a.city,
          postal_code: a.postalCode,
          google_maps_url: a.mapsUrl || undefined,
          is_billing_address: a.isBillingAddress,
        };

        if (a.id) {
          const snapA = originalSnapshot?.addresses.find((sa) => sa.id === a.id);
          const addressChanged =
            !snapA ||
            a.addressName !== snapA.addressName ||
            a.addressCategory !== snapA.addressCategory ||
            a.branch !== snapA.branch ||
            a.fullAddress !== snapA.fullAddress ||
            a.province !== snapA.province ||
            a.city !== snapA.city ||
            a.postalCode !== snapA.postalCode ||
            a.mapsUrl !== snapA.mapsUrl ||
            a.isBillingAddress !== snapA.isBillingAddress;

          if (addressChanged) {
            await updateVendorAddress({
              vendorId: id,
              addressId: a.id,
              transportResourceServiceV1UpdateVendorAddressBody: addressBody,
            }).unwrap();
          }
        } else {
          await createVendorAddress({
            vendorId: id,
            transportResourceServiceV1CreateVendorAddressBody: addressBody,
          }).unwrap();
        }
      }

      const vendorLogoFile = vendorInfoForm.getValues("vendorLogoFile");
      if (vendorLogoFile && id) {
        const formData = new FormData();
        formData.append("file", vendorLogoFile);
        await uploadLogo({
          id,
          transportResourceServiceV1UploadVendorLogoBody: formData as TransportResourceServiceV1UploadVendorLogoBody,
        }).unwrap();
      }

      dispatch(vendorApi.util.invalidateTags([{ type: "Vendor", id: "LIST" }]));
      refetch();
      setResultModal({
        open: true,
        title: "Vendor Successfully Updated!",
        message: "Vendor information has been successfully updated.",
        icon: "success",
      });
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseResultModal = () => {
    setResultModal((prev) => ({ ...prev, open: false }));
    navigate(`/master-data/vendor/${id}/detail`);
  };

  return {
    t,
    openSections,
    toggleSection,
    vendorInfoForm,
    vendorBranchForm,
    vendorAddressForm,
    branchItems,
    addressItems,
    branchOptions,
    hasBillingAddress,
    editingBranchIndex,
    editingAddressIndex,
    isLoading: isLoadingDetail || isUpdatingVendor,
    isSaving,
    isDirty,
    handleNextFromInformation,
    handleNextFromBranch,
    handleAddBranch,
    handleEditBranch,
    handleDeleteBranch,
    handleAddAddress,
    handleEditAddress,
    handleDeleteAddress,
    handleConfirmAddress,
    handleCancel,
    handleSave,
    resultModal,
    setResultModal,
    handleCloseResultModal,
  };
};

export default useEditVendor;
